import { doc, getDoc, serverTimestamp, updateDoc } from 'firebase/firestore';
import { Check, ChevronLeft, ChevronRight, MailIcon, Trash2 } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { firestore } from '../../firebase/firebase';
import useFilteredUsers from '../../hooks/useFilteredUsers';
import useUsers from '../../store/users';
import { formatDate, getRoleColor, getSubscriptionStatus, getTimeAgo, getUnlimitedColor, getUnlimitedOptions, getUnlimitedValue } from '../../utils/helpers';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../common/AlertDialog';
import Button from '../common/Button';
import { Dialog, DialogButton } from '../common/Dialog';
import FormSelect from '../common/FormSelect';
import Input from '../common/Input';
import FormTextArea from '../common/TextArea';
import ReferralRewardsManager from '../ReferralManagment/ReferralManagement';

const ITEMS_PER_PAGE = 15;

const Users = () => {
  const { users, loading, fetchUsers } = useUsers();
  const { sendNotificationEmail, isManager, isAdmin, role } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBy, setFilterBy] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [currentPage, setCurrentPage] = useState(1);
  const [createdAt, setCreatedAt] = useState('');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Filter and sort users
  const filteredUsers = useFilteredUsers(users, searchTerm, filterBy, sortBy, createdAt);

  useEffect(() => {
    fetchUsers();

  }, []);


  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterBy, sortBy]);
  const [referralSettings, setReferralSettings] = useState(null);
  const [loadingSettings, setLoadingSettings] = useState(true);

  // Add fetch for referral settings
  useEffect(() => {
    const fetchReferralSettings = async () => {
      try {
        const settingsDoc = await getDoc(doc(firestore, "referralRewards", "settings"));
        if (settingsDoc.exists()) {
          const res = settingsDoc.data();
          console.log("Referral settings:", res);

          setReferralSettings(res);
        }
      } catch (error) {
        console.error("Error fetching referral settings:", error);
      } finally {
        setLoadingSettings(false);
      }
    };

    fetchReferralSettings();
  }, []);

 

  const [userToDelete, setUserToDelete] = useState(null);
  const [editingCredits, setEditingCredits] = useState({});
  const [isUpdatingCredits, setIsUpdatingCredits] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState({});
  const [isUpdatingRole, setIsUpdatingRole] = useState({});
  const [isUpdatingUnlimited, setIsUpdatingUnlimited] = useState({});

  // Check if current user can modify target user
  const canModifyUser = (targetUser) => {
    if (isAdmin) return true;
    if (isManager && targetUser.role !== 'admin') return true;
    return false;
  };

  const handleUnlimitedChange = async (userId, value) => {
    const user = users.find(u => u.id === userId);
    if (!canModifyUser(user)) return;

    try {
      setIsUpdatingUnlimited(prev => ({ ...prev, [userId]: true }));

      const userRef = doc(firestore, "users", userId);
      const updateData = {
        subscriptionPeriod: value
      };

      if (value === "inactive") {
        updateData.unlimitedUntil = null;
      } else if (value === "lifetime") {
        updateData.unlimitedUntil = null;
      } else {
        const months = parseInt(value);
        const date = new Date();
        date.setMonth(date.getMonth() + months);
        updateData.unlimitedUntil = date;
      }

      updateData.updatedAt = serverTimestamp();
      await updateDoc(userRef, updateData);
      await fetchUsers();
    } catch (error) {
      console.error("Error updating unlimited status:", error);
      toast.error("Failed to update unlimited status");
    } finally {
      setIsUpdatingUnlimited(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleCreditUpdate = async (userId) => {
    const user = users.find(u => u.id === userId);
    if (!canModifyUser(user)) return;
    if (isUpdatingCredits) return;
    const newValue = editingCredits[userId];
    if (newValue < 0 || !newValue) return;
    try {
      setIsUpdatingCredits(true);
      const userRef = doc(firestore, "users", userId);
      await updateDoc(userRef, {
        credits: Number(newValue),
        updatedAt: serverTimestamp()
      });
      await fetchUsers();
      setEditingCredits(prev => ({
        ...prev,
        [userId]: undefined
      }));
    } catch (error) {
      console.error("Error updating credits:", error);
    } finally {
      setIsUpdatingCredits(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    if (!isAdmin) return; // Only admins can change roles

    try {
      setIsUpdatingRole(prev => ({ ...prev, [userId]: true }));
      const userRef = doc(firestore, 'users', userId);
      await updateDoc(userRef, {
        role: newRole,
        updatedAt: serverTimestamp()
      });
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
    } finally {
      setIsUpdatingRole(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    const user = users.find(u => u.id === userId);
    if (!canModifyUser(user)) return;

    try {
      setIsUpdatingStatus(prev => ({ ...prev, [userId]: true }));
      const userRef = doc(firestore, 'users', userId);
      await updateDoc(userRef, {
        disabled: newStatus === 'disabled',
        updatedAt: serverTimestamp()
      });
      await fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
    } finally {
      setIsUpdatingStatus(prev => ({ ...prev, [userId]: false }));
    }
  };

  const handleCreditChange = (userId, value) => {
    const user = users.find(u => u.id === userId);
    if (!canModifyUser(user)) return;

    setEditingCredits(prev => ({
      ...prev,
      [userId]: value
    }));
  };

  const handleDeleteUser = async () => {
    if (!userToDelete || !isAdmin) return; // Only admins can delete users
    try {
      await deleteDoc(doc(firestore, 'users', userToDelete));
      await fetchUsers();
      setShowDeleteDialog(false);
      setUserToDelete(null);
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  // const handleCreditUpdate = async (userId) => {
  //   const user = users.find(u => u.id === userId);
  //   if (!canModifyUser(user)) return;

  //   if (isUpdatingCredits) return;
  //   const newValue = editingCredits[userId];
  //   if (newValue < 0 || !newValue) return;

  //   try {
  //     setIsUpdatingCredits(true);
  //     const userRef = doc(firestore, 'users', userId);
  //     await updateDoc(userRef, {
  //       credits: Number(newValue),
  //       updatedAt: serverTimestamp()
  //     });
  //     await fetchUsers();
  //     setEditingCredits(prev => ({
  //       ...prev,
  //       [userId]: undefined
  //     }));
  //   } catch (error) {
  //     console.error('Error updating credits:', error);
  //   } finally {
  //     setIsUpdatingCredits(false);
  //   }
  // };


  const [openMailDialog, setOpenMailDialog] = useState(false);
  const [mailUser, setMailUser] = useState(null);
  const [mailData, setMailData] = useState({
    message: '',
    to: mailUser ? mailUser.email : ""
  });
  const [mailLoading, setMailLoading] = useState(false);
  const handleSetMailUser = (user) => {
    setOpenMailDialog(true);
    setMailUser(user);
  }
  const handleMailUser = async () => {
    try {

      setMailLoading(true);
      await sendNotificationEmail(mailUser.email, mailData.message);
      setOpenMailDialog(false);
      setMailData({ message: '', to: '' });
      setMailUser(null);
      toast.success('Email sent successfully');
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Error sending email');
    } finally {
      setMailLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">


      <Dialog
        open={openMailDialog}
        onClose={() => setOpenMailDialog(false)}
        title={`Email to ${mailUser?.email}`}
        description="Send an email to the user with the following details"
        size="md"
        footer={
          <>
            <DialogButton onClick={() => setOpenMailDialog(false)}>
              Cancel
            </DialogButton>
            <DialogButton variant="primary" onClick={handleMailUser} disabled={mailLoading}>
              {mailLoading ? "Sending..." : "Send"}
            </DialogButton>
          </>
        }
      >
        {/* Send email to mailUser now ask for the subject and message */}
        <div className="flex flex-col gap-4">

          <FormTextArea
            label="Message"
            type="text"
            value={mailData.message}
            onChange={(e) => setMailData({ ...mailData, message: e.target.value })}
            placeholder="Enter email message..."
            className="h-32 w-full"
          />
        </div>
      </Dialog>


      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <span className={` float-right text-sm font-semibold px-4 py-2 rounded-lg uppercase
                             ${getRoleColor(role)}`}>
          {role}
        </span>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Users Management</h1>
            <p className="text-gray-500 mt-1">
              {filteredUsers.length} users found
            </p>
          </div>

        </div>

        {/* Search and Filters */}
        <div className=" flex-wrap sm:flex sm:flex-nowrap justify-between gap-3 sm:gap-10 items-center">
          <div className="">
            <div className="relative">
              <Input
                label={'Search'}
                type="text"
                placeholder="Search by email or referral code..."
                className=" w-full sm:w-80 pr-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row align-center sm:gap-5 justify-between ">
            {/* Filter by joining date (createdAt) */}


            <Input
              label="Joined on"
              type="date"
              className=" px-4 py-2 w-full sm:w-52 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
              value={createdAt}
              onChange={(e) => setCreatedAt(e.target.value)}
            />

            <div className='flex items-end justify-between w-full gap-2 mt-2 sm:mt-0'>

              <div className='mt-auto'>
                <FormSelect
                  label="Filter By"
                  value={filterBy}
                  onChange={(e) => setFilterBy(e.target.value)}
                  className="px-4 py-2 w-40 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  wrapperClassName="mt-auto"
                >
                  <option value="all">All Users</option>
                  <option value="active">Active Users</option>
                  <option value="disabled">Disabled Users</option>
                  <option value="referred">Referred Users</option>
                  <option value="admin">Admins</option>
                  <option value="manager">Managers</option>
                </FormSelect>
              </div>


              <div className='mt-auto'>
                <FormSelect
                  label="Sort By"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-2 w-40 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-primary"
                  wrapperClassName="mt-auto"
                >
                  <option value="newest">Newest</option>
                  <option value="oldest">Oldest</option>
                  <option value="credits">Credits</option>
                </FormSelect>


              </div>
            </div>
          </div>
        </div>
        <br />
        {/* Users Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="min-w-full overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left  text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-3 text-left text-xs  font-medium text-gray-500 uppercase tracking-wider">
                    Credits & Usage
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Was Referred
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Lifetime Credits
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Unlimited
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Reffered Colleages
                  </th>

                  <th className="text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Info
                  </th>
                  {isAdmin ? <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th> : null}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={10} className="px-6 py-4 text-center">
                      <div className="flex justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
                      </div>
                    </td>
                  </tr>
                ) : paginatedUsers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center">
                      No users found
                    </td>
                  </tr>
                ) : (
                  paginatedUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                            <MailIcon className="h-5 w-5 text-gray-500 hover:text-yellow-400 cursor-pointer"
                              onClick={() => handleSetMailUser(user)} />
                          </div>
                          <div className="ml-4">
                            <div className="font-medium text-gray-900">{user.email}</div>
                            <div className="text-sm text-gray-500">Refferal : {user.referralCode}</div>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <div className="flex items-center">
                            <input
                              type="number"
                              className="border border-gray-200 rounded-l-md p-1 text-center w-16"
                              value={editingCredits[user.id] !== undefined ? editingCredits[user.id] : user.credits}
                              onChange={(e) => handleCreditChange(user.id, e.target.value)}
                              disabled={!canModifyUser(user)}
                              min="0"
                            />
                            <button
                              className={`border border-l-0 border-gray-200 rounded-r-md px-2 py-1 hover:bg-gray-50 disabled:opacity-40`}
                              onClick={() => handleCreditUpdate(user.id)}
                              disabled={!canModifyUser(user) || editingCredits[user.id] === user.credits || editingCredits[user.id] === undefined || isUpdatingCredits}
                            >
                              <Check className="h-4 w-4 text-green-700" />
                            </button>
                          </div>
                        </div>
                        <div className="text-xs text-center mr-8  text-gray-500">
                          Searches: {user.searchCount || 0}
                        </div>
                      </td>
                      <td className="whitespace-nowrap text-center">
                        <select
                          className={`text-xs font-semibold px-3 py-1 rounded-full 
                      ${user.disabled ? 'bg-red-100 text-red-800 hover:bg-red-200' : 'bg-green-100 text-green-800 hover:bg-green-200'} 
                      cursor-pointer transition-colors duration-200 ease-in-out`}
                          value={user.disabled ? 'disabled' : 'active'}
                          onChange={(e) => handleStatusChange(user.id, e.target.value)}
                          disabled={!canModifyUser(user) || isUpdatingStatus[user.id]}
                        >
                          <option value="active" className="bg-white text-gray-800">Active</option>
                          <option value="disabled" className="bg-white text-gray-800">Disabled</option>
                        </select>
                        {isUpdatingStatus[user.id] && (
                          <div className="mt-1">
                            <div className="animate-spin inline-block w-3 h-3 border-[2px] border-current border-t-transparent text-gray-400 rounded-full" />
                          </div>
                        )}
                      </td>
                      <td
                        className="whitespace-nowrap text-center">
                        {user.referrerId ?
                          <span className="px-2 inline-flex  text-center   ">
                            Yes
                          </span>
                          :
                          <span className="px-2 inline-flex  text-center   ">
                            No
                          </span>
                        }
                      </td>
                      <td className="whitespace-nowrap text-center">
                        {user.subscriptionPeriod === 'lifetime'
                          ? 'Yes' : 'No'}
                      </td>
                      {/* <td className="whitespace-nowrap text-center px-10">
                        <select
                          className={`text-xs font-semibold px-3 py-1 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${getUnlimitedColor(getUnlimitedValue(user))}`}
                          value={getUnlimitedValue(user)}
                          onChange={(e) => handleUnlimitedChange(user.id, e.target.value)}
                          disabled={!canModifyUser(user) || isUpdatingUnlimited[user.id]}
                        >
                          <option value="inactive" className="bg-white text-gray-800">Inactive</option>
                          <option value="3" className="bg-white text-gray-800">3 Months</option>
                          <option value="6" className="bg-white text-gray-800">6 Months</option>
                          <option value="12" className="bg-white text-gray-800">12 Months</option>
                          <option value="lifetime" className="bg-white text-gray-800">Lifetime</option>
                        </select>
                        {isUpdatingUnlimited[user.id] && (
                          <div className="mt-1">
                            <div className="animate-spin inline-block w-3 h-3 border-[2px] border-current border-t-transparent text-gray-400 rounded-full" />
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          {getSubscriptionStatus(user)}
                        </div>
                      </td> */}
                      <td className="whitespace-nowrap text-center px-10">
                        <select
                          className={`text-xs font-semibold px-3 py-1 rounded-full cursor-pointer transition-colors duration-200 ease-in-out ${getUnlimitedColor(getUnlimitedValue(user))}`}
                          value={getUnlimitedValue(user)}
                          onChange={(e) => handleUnlimitedChange(user.id, e.target.value)}
                          disabled={!canModifyUser(user) || isUpdatingUnlimited[user.id]}
                        >
                          {getUnlimitedOptions(referralSettings).map(option => (
                            <option
                              key={option.value}
                              value={option.value}
                              className="bg-white text-gray-800"
                            >
                              {option.label}

                            </option>
                          ))}
                        </select>
                        {isUpdatingUnlimited[user.id] && (
                          <div className="mt-1">
                            <div className="animate-spin inline-block w-3 h-3 border-[2px] border-current border-t-transparent text-gray-400 rounded-full" />
                          </div>
                        )}
                        <div className="text-xs text-gray-500 mt-1">
                          {getSubscriptionStatus(user)}

                        </div>
                      </td>
                      <td className="text-center">
                        <select
                          className={`text-xs font-semibold px-3 py-1 rounded-full cursor-pointer transition-colors
                             duration-200 ease-in-out ${getRoleColor(user.role)}`}
                          value={user.role === 'admin' ? 'admin' : user.role === 'manager' ? 'manager' : 'user'}
                          onChange={(e) => handleRoleChange(user.id, e.target.value)}
                          disabled={!isAdmin || isUpdatingRole[user.id]}
                        >
                          <option value="user" className="bg-white text-gray-800">User</option>
                          <option value="manager" className="bg-white text-gray-800">Manager</option>
                          <option value="admin" className="bg-white text-gray-800">Admin</option>
                        </select>
                        {isUpdatingRole[user.id] && (
                          <div className="mt-1">
                            <div className="animate-spin inline-block w-3 h-3 border-[2px] border-current border-t-transparent text-gray-400 rounded-full" />
                          </div>
                        )}
                      </td>
                      <td className="text-center">

                        <span>{user.referralsCount || 0}</span>

                      </td>
                      <td className="sm:px-0">
                        <div className="text-xs text-gray-500 text-center w-fit min-w-32">
                          <div>Joined: {formatDate(user.createdAt)}</div>
                          <div>Last search: {user.lastCreditDeduction ? getTimeAgo(user.lastCreditDeduction) :
                            "Never"
                          }</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                        {isAdmin && (
                          <div className="flex gap-2 flex-col justify-between space-x-1">
                            <Trash2
                              className="h-8 w-8 rounded-md cursor-pointer bg-brand-primary p-2 text-center mx-auto text-white"
                              onClick={() => {
                                setUserToDelete(user.id);
                                setShowDeleteDialog(true);
                              }}
                            />
                          </div>
                        )}
                      </td>

                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {filteredUsers.length > 0 && (
            <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
              <div className="flex-1 flex justify-between sm:hidden">
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="xs"
                  onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min(startIndex + ITEMS_PER_PAGE, filteredUsers.length)}
                    </span> of{' '}
                    <span className="font-medium">{filteredUsers.length}</span> results
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    leftIcon={<ChevronLeft className="h-4 w-4" />}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    rightIcon={<ChevronRight className="h-4 w-4" />}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        <br />
        <ReferralRewardsManager />
      </div>
      <DeleteAlert handleDeleteUser={handleDeleteUser} setShowDeleteDialog={setShowDeleteDialog} showDeleteDialog={showDeleteDialog} />
    </div>
  );
};


const DeleteAlert = ({ handleDeleteUser, setShowDeleteDialog, showDeleteDialog }) => {
  return (
    <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This action cannot be undone. This will permanently delete the user account
            and remove all associated data.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={() => setShowDeleteDialog(false)}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-red-600 text-white hover:bg-red-700"
            onClick={handleDeleteUser}
          >
            Delete User
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

export default Users;